import { HttpErrorResponse } from '@angular/common/http';

/** Client-error statuses that still ask for another attempt rather than answer the question. */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 425, 429]);

/** Retries on top of the initial attempt, so a transient failure costs at most 3 requests. */
export const TRANSIENT_RETRY_COUNT = 2;

/**
 * A transient failure is one a repeat attempt could plausibly fix: the request never got a
 * response (status 0 — offline, aborted, DNS/TLS failure, CORS), the server explicitly asked
 * us to come back (408/425/429), or the server itself failed (5xx).
 *
 * Everything else is a definitive answer and must not be retried — any other 4xx, and any
 * non-HTTP throw such as a contract-validation error from `parseResponse`.
 */
export function isTransientHttpError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
        return false;
    }

    return error.status === 0 || error.status >= 500 || RETRYABLE_CLIENT_STATUSES.has(error.status);
}

/**
 * A definitive refusal from the server: a 4xx that is not one of the "try again" statuses.
 * This is the only failure shape allowed to mean "the account does not have this feature".
 */
export function isClientRejection(error: unknown): boolean {
    return (
        error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500 && !isTransientHttpError(error)
    );
}

/**
 * TanStack Query `retry` predicate — repeat transient failures a couple of times, give up
 * immediately on anything definitive. `failureCount` is the number of failures *before* the
 * current one, so this yields `TRANSIENT_RETRY_COUNT` retries.
 */
export function retryTransient(failureCount: number, error: unknown): boolean {
    return failureCount < TRANSIENT_RETRY_COUNT && isTransientHttpError(error);
}
